import axios from 'axios'
import qs from 'qs'
import { IUserData } from '../utils/Auth0'
import { isDummyUser } from '../utils/utils'
import { AuthProfile } from './profile/profile'

// Extend AxiosRequestConfig to include our custom property
declare module 'axios' {
    interface AxiosRequestConfig {
        useImitatorForSalaryApi?: boolean
    }
}

const oidcSettings = {
    authority: process.env.REACT_APP_AUTH_AUTHORITY,
    client_id: process.env.REACT_APP_AUTH_CLIENT_ID,
    redirect_uri: process.env.REACT_APP_AUTH_REDIRECT_URI,
    response_type: process.env.REACT_APP_AUTH_RESPONSE_TYPE,
    scope: process.env.REACT_APP_AUTH_REDIRECT_URI_SCOPE,
}

const baseURL = process.env.REACT_APP_API_BASE_URL
const dummyBaseURL = process.env.REACT_APP_API_BASE_URL_DUMMY_USER

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function parseJwt(token) {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split('')
            .map(function (c: any) {
                return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`
            })
            .join('')
    )

    return JSON.parse(jsonPayload)
}

// Function to get baseURL based on userID
const getBaseURL = (): string => {
    const userData = localStorage.getItem('user_data')

    if (!userData) {
        return baseURL
    }
    const user: IUserData = JSON.parse(userData)
    const jwt = parseJwt(user.access_token)
    const { upn } = jwt

    // If user is dummy user, return dummyBaseURL.
    if (isDummyUser(upn)) {
        return dummyBaseURL
    }
    return baseURL
}

const axiosWithAuth = axios.create({
    baseURL: getBaseURL(),
})

export const doRefreshToken = async (userData: IUserData): Promise<void> => {
    axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

    try {
        const response = await axios.post(
            `${oidcSettings.authority}/oauth2/token`,
            qs.stringify({
                grant_type: 'refresh_token',
                redirect_uri: oidcSettings.redirect_uri,
                client_id: oidcSettings.client_id,
                refresh_token: userData.refresh_token,
            })
        )

        const user: IUserData = response.data
        const updatedUserDate = userData

        updatedUserDate.access_token = user.access_token

        localStorage.setItem('user_data', JSON.stringify(updatedUserDate))
    } catch {
        localStorage.clear()
        window.location.href = '/'
    }
}

axiosWithAuth.interceptors.request.use(
    async (config) => {
        const localUser = localStorage.getItem('user_data')
        const isDummyUserFlag = localStorage.getItem('isDummyUser')

        if (isDummyUserFlag) {
            // Sett axios base url to dummy url
            // eslint-disable-next-line no-param-reassign
            // config.baseURL = dummyBaseURL
        }
        const user: IUserData = JSON.parse(localUser)
        // eslint-disable-next-line no-param-reassign
        config.headers = {
            Authorization: `Bearer ${user.access_token}`,
        }

        return config
    },
    (error) => {
        Promise.reject(error)
    }
)

axiosWithAuth.interceptors.response.use(
    (response) => {
        return response
    },
    (error) => {
        if (error.response.status === 401) {
            // try refresh token.
            const localUser = localStorage.getItem('user_data')
            const user: IUserData = JSON.parse(localUser)

            if (user.access_token && user.refresh_token) {
                doRefreshToken(user)
            } else {
                localStorage.clear()
                window.location.href = '/'
            }
        }
        throw error
    }
)

const axiosWithImitator = axiosWithAuth

axiosWithImitator.interceptors.request.use(
    async (config) => {
        const profile = localStorage.getItem('profile_data')
        const user: AuthProfile = JSON.parse(profile)

        if (
            user &&
            user.isImitator &&
            user.apiProfile &&
            user.apiProfile.myProfileId
        ) {
            const paramName = config.useImitatorForSalaryApi
                ? 'ImiteretBrugerMedlemIdent'
                : 'request.imiteretBrugerMedlemIdent'

            // For POST, PUT, DELETE requests when useImitatorForSalaryApi is true
            if (
                config.useImitatorForSalaryApi &&
                ['post', 'put', 'delete'].includes(
                    config.method?.toLowerCase() || ''
                )
            ) {
                if (config.data) {
                    // If data exists, add the profile ID to it
                    // eslint-disable-next-line no-param-reassign
                    config.data = {
                        ...config.data,
                        [paramName]: user.apiProfile.myProfileId,
                    }
                } else {
                    // If no data exists, create new data object
                    // eslint-disable-next-line no-param-reassign
                    config.data = {
                        [paramName]: user.apiProfile.myProfileId,
                    }
                }
            } else if (config.params) {
                // For other cases with existing params
                // eslint-disable-next-line no-param-reassign
                config.params[paramName] = user.apiProfile.myProfileId
            } else {
                // For other cases without params
                // eslint-disable-next-line no-param-reassign
                config.params = {
                    [paramName]: user.apiProfile.myProfileId,
                }
            }
        }

        return config
    },
    (error) => {
        Promise.reject(error)
    }
)

// Response interceptor for API calls
// axiosApiInstance.interceptors.response.use(
//     (response) => {
//         return response
//     },
//     async function (error) {
//         const originalRequest = error.config
//         const localUser = localStorage.getItem('user_data')
//         const user: IUserData = JSON.parse(localUser)
//         if (error.response.status === 403 && !originalRequest._retry) {
//             // originalRequest._retry = true
//             // const access_token = await refreshAccessToken()
//             // axios.defaults.headers.common.Authorization =
// `Bearer ${access_token}`
//             return axiosApiInstance(originalRequest)
//         }
//         return Promise.reject(error)
//     }
// )

axiosWithAuth.interceptors.request.use(
    async (config) => {
        const newConfig = config
        // Dynamically get the baseURL on every request
        const currentBaseURL = getBaseURL()
        // Only override baseURL if skipBaseURLOverride is not set to true
        if (
            newConfig.baseURL !== currentBaseURL &&
            !('skipBaseURLOverride' in config)
        ) {
            newConfig.baseURL = currentBaseURL
        }

        // Retrieve user data for Authorization header
        const localUser = localStorage.getItem('user_data')
        if (localUser) {
            const user: IUserData = JSON.parse(localUser)
            newConfig.headers = {
                ...config.headers,
                Authorization: `Bearer ${user.access_token}`,
            }
        }

        return newConfig
    },
    (error) => {
        return Promise.reject(error)
    }
)

export { axiosWithAuth, axiosWithImitator }
